import {
  CheckCircleOutlined,
  EyeOutlined,
  IssuesCloseOutlined,
  RedoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Space, TableColumnsType, Tooltip, Typography } from "antd";
import {
  MeasurementTank,
  ResponseInventoryControlRegistry,
  WarehouseInventoryControl,
} from "api/generated";
import moment from "moment";
import "moment/locale/es";
import { areEquals, volumeFormatter } from "services/utils/formatterUtil";

export interface DailyStockControlData {
  key: React.Key;
  pdd: string;
  id: string;
  gicDate: moment.Moment;
  initialVolume?: number;
  inputs?: number;
  sales?: number;
  calculatedVolume?: number;
  inventoryVolume?: number;
  differenceLitters?: number;
  differencePercentage?: number;
  differenceReason?: string;
  differenceSolution?: string;
  hasArrivingLateInformation: boolean;
  wic: WarehouseInventoryControl[];
  isEdited: boolean;
  isEditable: boolean;
}

const ConditionalErrorSpan = ({
  children,
  condition,
}: {
  children: React.ReactNode;
  condition: boolean;
}) => <span style={condition ? { color: "#FF7171" } : {}}>{children}</span>;

export interface ColumnActions {
  onViewCause?: (record: DailyStockControlData) => void;
  onEditCause?: (record: DailyStockControlData) => void;
  onRecalculate?: (record: DailyStockControlData) => void;
  onViewDetails?: (record: DailyStockControlData) => void;
  onRegisterManualMeasurement?: (record: DailyStockControlData) => void;
  showActions: boolean;
  isMobile?: boolean;
}

export interface ColumnActionsByPeriod {
  onRecalculatePeriod?: (record: ResponseInventoryControlRegistry) => void;
  onViewDetails?: (record: ResponseInventoryControlRegistry) => void;
  onRegisterManualMeasurement?: (
    record: ResponseInventoryControlRegistry
  ) => void;
  isMobile?: boolean;
  showRegenerate: boolean;
}

export const renderStatusIcon = (
  gicRecord?: DailyStockControlData,
  wicRecord?: WarehouseInventoryControl
) => {
  const warningTooltipTitle = [];
  const isAnyWicInvalid = gicRecord?.wic.every(
    (item) => item.invalidLastVolumeRegistry
  );
  if (isAnyWicInvalid || wicRecord?.invalidLastVolumeRegistry) {
    warningTooltipTitle.push("Mediciones de nivel invalidas");
  }
  if (gicRecord?.hasArrivingLateInformation) {
    warningTooltipTitle.push("Hay nueva información operativa");
  }

  if (warningTooltipTitle.length === 0) {
    return (
      <Tooltip title="Mediciones validas">
        <CheckCircleOutlined style={{ color: "green", fontSize: "20px" }} />
      </Tooltip>
    );
  } else {
    return (
      <Tooltip
        title={warningTooltipTitle.join("\n")}
        overlayStyle={{ whiteSpace: "pre-line" }}
      >
        <WarningOutlined style={{ color: "orange", fontSize: "20px" }} />
      </Tooltip>
    );
  }
};

// Función para verificar si es el último día del mes
function isLastDayOfMonth(date: moment.Moment) {
  const endOfMonth = date.clone().endOf("month");
  return date.format("DD/MM/YYYY") === endOfMonth.format("DD/MM/YYYY");
}

export const getStockControlColumns = (
  actions: ColumnActions
): TableColumnsType<DailyStockControlData> => {
  const baseColumns: TableColumnsType<DailyStockControlData> = [
    {
      title: "Fecha",
      dataIndex: "gicDate",
      key: "gicDate",
      fixed: actions.showActions ? "left" : undefined,
      render: (date) => date.format("DD/MM/YYYY"),
    },
    {
      title: "Volumen inicial",
      dataIndex: "initialVolume",
      key: "initialVolume",
      render: (initialVolume) => volumeFormatter(initialVolume),
    },
    {
      title: "Entradas",
      dataIndex: "inputs",
      key: "inputs",
      width: 100,
      render: (inputs, record) => (
        <ConditionalErrorSpan condition={record.hasArrivingLateInformation}>
          {volumeFormatter(inputs)}
        </ConditionalErrorSpan>
      ),
    },
    {
      title: "Salidas",
      dataIndex: "sales",
      key: "sales",
      width: 100,
      render: (sales, record) => (
        <ConditionalErrorSpan condition={record.hasArrivingLateInformation}>
          {volumeFormatter(sales)}
        </ConditionalErrorSpan>
      ),
    },
    {
      title: "Volumen calculado",
      dataIndex: "calculatedVolume",
      key: "calculatedVolume",
      render: (calculatedVolume, record) => (
        <ConditionalErrorSpan condition={record.hasArrivingLateInformation}>
          {volumeFormatter(calculatedVolume)}
        </ConditionalErrorSpan>
      ),
    },
    {
      title: "Inventario fisico",
      dataIndex: "inventoryVolume",
      key: "inventoryVolume",
      render: (volume, record) => (
        <Space>
          {renderStatusIcon(record)}
          {volumeFormatter(volume)}
        </Space>
      ),
    },
    {
      title: "Litros de diferencia",
      dataIndex: "differenceLitters",
      key: "differenceLitters",
      render: (difference) => (
        <ConditionalErrorSpan condition={difference < -1 || difference > 1}>
          {volumeFormatter(difference)} {difference !== null ? " Litros" : ""}
        </ConditionalErrorSpan>
      ),
    },
    {
      title: "Porcentaje de diferencia",
      dataIndex: "differencePercentage",
      key: "differencePercentage",
      width: 150,
      render: (difference) => (
        <ConditionalErrorSpan condition={difference < -1 || difference > 1}>
          {volumeFormatter(difference)} {" %"}
        </ConditionalErrorSpan>
      ),
    },
  ];

  // Solo agregar la columna de acciones si se solicitan
  if (actions.showActions !== false) {
    const actionsColumn: TableColumnsType<DailyStockControlData>[number] = {
      title: "Acciones",
      key: "operation",
      fixed: actions.isMobile ? undefined : "right",
      render: (_, record) => {
        const { isEdited, isEditable, differenceLitters } = record;
        const volumeDifference = Number(differenceLitters);
        const showRegisterCause = volumeDifference < -1 || volumeDifference > 1;

        // Verificar condiciones para mostrar el botón de medición manual
        const isLastDay = isLastDayOfMonth(record.gicDate);
        const hasInvalidMeasurements = record.wic.some(
          (item) => item.invalidLastVolumeRegistry
        );
        const showManualMeasurement = isLastDay && hasInvalidMeasurements;

        return (
          <Space>
            {/* Botón para ver detalles */}
            <Tooltip title="Ver detalles">
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  actions.onViewDetails?.(record);
                }}
              />
            </Tooltip>

            {/* Botón para registrar medición manual */}
            {showManualMeasurement && (
              <Tooltip
                title={
                  isEditable
                    ? "Registrar medición de nivel manual"
                    : "Inserción deshabilitada, marcado como enviado al SAT"
                }
              >
                <Button
                  type="primary"
                  onClick={() => actions.onRegisterManualMeasurement?.(record)}
                  disabled={!isEditable}
                >
                  Registrar medición
                </Button>
              </Tooltip>
            )}

            {/* Botones condicionales según estado del gic */}
            {isEditable && showRegisterCause && (
              <Tooltip title={!isEdited ? "Registrar causa" : "Editar causa"}>
                <Button
                  icon={<IssuesCloseOutlined />}
                  onClick={() => actions.onEditCause?.(record)}
                />
              </Tooltip>
            )}

            {
              <Tooltip
                title={
                  isEditable
                    ? "Regenerar Control de Existencias"
                    : "Regeneración deshabilitada, marcado como enviado al SAT"
                }
              >
                <Button
                  icon={<RedoOutlined />}
                  style={{ color: "green" }}
                  onClick={() => actions.onRecalculate?.(record)}
                  disabled={!isEditable}
                />
              </Tooltip>
            }

            {!isEditable && showRegisterCause && (
              <Tooltip title="Ver causa">
                <Button onClick={() => actions.onViewCause?.(record)}>
                  Ver causa
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    };

    return [...baseColumns, actionsColumn];
  }

  return baseColumns;
};

export const getWarehouseStockColumns =
  (): TableColumnsType<WarehouseInventoryControl> => {
    const baseColumns: TableColumnsType<WarehouseInventoryControl> = [
      {
        title: "Almacén",
        dataIndex: "warehouse",
        key: "warehouse",
        width: 100,
        render: (warehouse) => warehouse.warehouseKey,
      },
      {
        title: "Volumen inicial del día",
        dataIndex: "dailyInitialVolume",
        key: "dailyInitialVolume",
        width: 100,
        render: (volume) => volumeFormatter(volume),
      },
      {
        title: "Entradas",
        dataIndex: "dailyInputs",
        key: "dailyInputs",
        width: 100,
        render: (inputs) => volumeFormatter(inputs),
      },
      {
        title: "Salidas",
        dataIndex: "dailySales",
        key: "dailySales",
        width: 100,
        render: (sales) => volumeFormatter(sales),
      },
      {
        title: "Volumen calculado al final del día (Lógico)",
        dataIndex: "dailyFinalVolume",
        key: "dailyFinalVolume",
        width: 100,
        render: (volume) => volumeFormatter(volume),
      },
      {
        title: "Volumen final del día (Inventario Físico)",
        dataIndex: "dailyStaticVolume",
        key: "dailyStaticVolume",
        width: 100,
        render: (volume) => volumeFormatter(volume),
      },
      {
        title: "Status",
        width: 100,
        render: (record) => renderStatusIcon(undefined, record),
      },
    ];

    return baseColumns;
  };

export const getContainerStockColumns =
  (): TableColumnsType<MeasurementTank> => {
    const baseColumns: TableColumnsType<MeasurementTank> = [
      {
        title: "Código del tanque",
        dataIndex: "tankCode",
        key: "tankCode",
        width: 100,
      },
      {
        title: "Volumen reportado",
        dataIndex: "volume",
        key: "volume",
        width: 100,
        render: (volume) => volumeFormatter(volume),
      },
      {
        title: "Momento de la medición",
        dataIndex: "measurementDate",
        key: "measurementDate",
        width: 100,
      },
      {
        title: "Resultado",
        dataIndex: "invalid",
        key: "invalid",
        width: 100,
        render: (isInvalid) =>
          !isInvalid ? (
            <Typography.Text style={{ color: "green" }}>Valida</Typography.Text>
          ) : (
            <Typography.Text type="danger" strong>
              Invalida
            </Typography.Text>
          ),
      },
      {
        title: "Mensaje Error",
        width: 100,
        render: (item) =>
          item.invalid ? (
            <Typography.Text type="danger" strong>
              {item.messageError}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary">
              {item.messageError}
            </Typography.Text>
          ),
      },
      {
        title: "Tipo de medición",
        dataIndex: "type",
        key: "type",
        width: 100,
        render: (text) =>
          text ? (
            text
          ) : (
            <Typography.Text type="danger" strong>
              N/A
            </Typography.Text>
          ),
      },
    ];

    return baseColumns;
  };

// Función para verificar si el mes está completo
function isCompleteMonth(date: moment.Moment) {
  const currentDate = moment();
  const recordDate = moment(date);

  // Verificar si el mes del registro es anterior al mes actual
  return recordDate.isBefore(currentDate, "month");
}

// Función para verificar si hay inconsistencias en el registro
function hasInconsistencies(record: ResponseInventoryControlRegistry): boolean {
  // Calcular total de entradas operativas
  const calculatedTotalInputs =
    (record.calculatedTotalReceptions || 0) +
    (record.calculatedTotalTraspasos || 0);

  return (
    !areEquals(record.totalInputs, calculatedTotalInputs) ||
    !areEquals(record.totalOutputs, record.calculatedTotalSales) ||
    !areEquals(record.finalVolume, record.calculatedFinalVolume) ||
    !areEquals(
      record.finalStaticVolume,
      record.calculatedLatestMeasurementVolume
    ) ||
    !areEquals(record.volumeDifference, record.calculatedVolumeDifference) ||
    !areEquals(
      record.percentageDifference,
      record.calculatedPercentageDifference
    )
  );
}

export const getStockControlByPeriodColumns = (
  actions: ColumnActionsByPeriod
): TableColumnsType<ResponseInventoryControlRegistry> => {
  const baseColumns: TableColumnsType<ResponseInventoryControlRegistry> = [
    {
      title: "PDD",
      dataIndex: "distributionPointName",
      key: "distributionPointName",
    },
    {
      title: "Periodo",
      dataIndex: "registeredDate",
      key: "registeredDate",
      fixed: "left",
      render: (date) => {
        const fecha = moment(date).locale("es");
        return fecha.format("MMMM/YYYY");
      },
    },
    {
      title: "Volumen inicial",
      dataIndex: "initialVolume",
      key: "initialVolume",
      render: (initialVolume) => volumeFormatter(initialVolume),
    },
    {
      title: "Total Entradas",
      dataIndex: "totalInputs",
      key: "totalInputs",
      width: 100,
      render: (inputs) => volumeFormatter(inputs),
    },
    {
      title: "Total Salidas",
      dataIndex: "totalOutputs",
      key: "totalOutputs",
      width: 100,
      render: (totalOutputs) => volumeFormatter(totalOutputs),
    },
    {
      title: "Volumen calculado",
      dataIndex: "finalVolume",
      key: "finalVolume",
      render: (finalVolume) => volumeFormatter(finalVolume),
    },
    {
      title: "Inventario fisico",
      dataIndex: "finalStaticVolume",
      key: "finalStaticVolume",
      render: (finalStaticVolume) => volumeFormatter(finalStaticVolume),
    },
    {
      title: "Litros de diferencia",
      dataIndex: "volumeDifference",
      key: "volumeDifference",
      render: (volumeDifference) => (
        <ConditionalErrorSpan condition={Math.abs(volumeDifference ?? 0) > 1}>
          {volumeFormatter(volumeDifference)} Litros
        </ConditionalErrorSpan>
      ),
    },
    {
      title: "Porcentaje de diferencia",
      dataIndex: "percentageDifference",
      key: "percentageDifference",
      width: 150,
      render: (percentageDifference) => (
        <ConditionalErrorSpan
          condition={Math.abs(percentageDifference ?? 0) > 1}
        >
          {volumeFormatter(percentageDifference)} {" %"}
        </ConditionalErrorSpan>
      ),
    },
    {
      title: "Acciones",
      key: "operation",
      fixed: actions.isMobile ? undefined : "right",
      render: (_, record) => {
        // Verificar condiciones para mostrar el botón de medición manual
        const isComplete = isCompleteMonth(moment(record.registeredDate));
        const hasZeroVolume = record.calculatedLatestMeasurementVolume === 0;
        const showManualMeasurement = isComplete && hasZeroVolume;
        const isSentToSAT: boolean = record.isSentToSat ?? false;

        return (
          <Space>
            <Tooltip title="Ver detalles">
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  actions.onViewDetails?.(record);
                }}
              />
            </Tooltip>

            {/* Botón para registrar medición manual */}
            {showManualMeasurement && (
              <Tooltip
                title={
                  isSentToSAT
                    ? "Inserción deshabilitada, marcado como enviado al SAT"
                    : "Registrar medición de nivel manual"
                }
              >
                <Button
                  type="primary"
                  onClick={() => actions.onRegisterManualMeasurement?.(record)}
                  disabled={isSentToSAT}
                >
                  Registrar medición
                </Button>
              </Tooltip>
            )}

            {actions.showRegenerate && (
              <Tooltip
                title={
                  isSentToSAT
                    ? "Regeneración deshabilitada, marcado como enviado al SAT"
                    : "Regenerar controles de Existencias"
                }
              >
                <Button
                  icon={<RedoOutlined />}
                  style={{ color: "green" }}
                  onClick={() => actions.onRecalculatePeriod?.(record)}
                  disabled={isSentToSAT}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return baseColumns;
};

// Función para obtener el className de la fila basado en inconsistencias
export const getRowClassNameByPeriod = (
  record: ResponseInventoryControlRegistry
) => {
  if (hasInconsistencies(record)) {
    return "row-with-inconsistencies";
  }
  return "";
};
